import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Legend, CartesianGrid,
} from 'recharts';
import { Users, UserCheck, TrendingUp, BookOpen, Search, Calendar, Sparkles, Activity } from 'lucide-react';

const STAGE_COLORS = { Explore: '#2B6B7F', Engage: '#5FA8C1', Extend: '#7BA888' };

const fmtShortDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const StatCard = ({ icon: Icon, label, value, color, testId }) => (
  <Card className="card-hover" data-testid={testId}>
    <CardContent className="pt-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-600 mb-1">{label}</p>
          <p className="text-3xl font-bold" style={{ color }}>{value}</p>
        </div>
        <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: `${color}1A` }}>
          <Icon className="w-6 h-6" style={{ color }} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const SectionHeader = ({ icon: Icon, color, title, description }) => (
  <div className="flex items-start gap-3 mb-4">
    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}1A` }}>
      <Icon className="w-5 h-5" style={{ color }} />
    </div>
    <div>
      <h3 className="text-xl font-heading font-bold text-slate-800">{title}</h3>
      {description && <p className="text-sm text-slate-600">{description}</p>}
    </div>
  </div>
);

export const AdminAnalytics = () => {
  const { API } = useContext(AuthContext);
  const [engagement, setEngagement] = useState(null);
  const [growth, setGrowth] = useState(null);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [e, g, c] = await Promise.all([
          axios.get(`${API}/admin/analytics/engagement`),
          axios.get(`${API}/admin/analytics/spiritual-growth`),
          axios.get(`${API}/admin/analytics/content`),
        ]);
        setEngagement(e.data);
        setGrowth(g.data);
        setContent(c.data);
      } catch (err) {
        console.error('Failed to load analytics:', err);
        setError('Failed to load analytics. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [API]);

  if (loading) {
    return (
      <Card data-testid="analytics-loading">
        <CardContent className="py-12 text-center text-slate-500">
          Loading analytics...
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card data-testid="analytics-error">
        <CardContent className="py-8 text-center text-red-600">{error}</CardContent>
      </Card>
    );
  }

  const signupTimeseries = (engagement?.signups_timeseries || []).map((d) => ({
    ...d,
    label: fmtShortDate(d.date),
  }));

  const journalsPerWeek = (growth?.journals_per_week || []).map((w) => ({
    ...w,
    label: fmtShortDate(w.week_start),
  }));

  const stageData = growth?.stage_distribution || [];

  return (
    <div className="space-y-8" data-testid="admin-analytics">
      {/* Engagement section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl font-heading text-[#2B6B7F] flex items-center gap-2">
            <Activity className="w-6 h-6" />
            User Engagement
          </CardTitle>
          <CardDescription>Sign-ups, daily / weekly active users, and growth trend (last 30 days)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={Users} label="Total Users" value={engagement?.total_users ?? 0} color="#2B6B7F" testId="analytics-total-users" />
            <StatCard icon={UserCheck} label="Active Today (DAU)" value={engagement?.dau ?? 0} color="#5FA8C1" testId="analytics-dau" />
            <StatCard icon={TrendingUp} label="Active This Week (WAU)" value={engagement?.wau ?? 0} color="#7BA888" testId="analytics-wau" />
            <StatCard icon={Sparkles} label="New Sign-ups (30d)" value={engagement?.new_users_30d ?? 0} color="#D4A574" testId="analytics-new-users" />
          </div>

          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <h4 className="text-sm font-semibold text-slate-700 mb-4">Sign-ups over the last 30 days</h4>
            <div style={{ width: '100%', height: 240 }}>
              <ResponsiveContainer>
                <LineChart data={signupTimeseries} margin={{ top: 8, right: 16, left: -8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} interval={Math.max(0, Math.floor(signupTimeseries.length / 8) - 1)} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#64748b' }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" stroke="#2B6B7F" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Spiritual Growth section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl font-heading text-[#2B6B7F] flex items-center gap-2">
            <TrendingUp className="w-6 h-6" />
            Spiritual Growth
          </CardTitle>
          <CardDescription>Discipleship stages, journaling streaks, and weekly journal volume</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={BookOpen} label="Total Journals" value={growth?.total_journals ?? 0} color="#7BA888" testId="analytics-total-journals" />
            <StatCard icon={Calendar} label="Total Check-ins" value={growth?.total_checkins ?? 0} color="#D4A574" testId="analytics-total-checkins" />
            <StatCard icon={TrendingUp} label="Avg Journal Streak" value={growth?.avg_journal_streak ?? 0} color="#5FA8C1" testId="analytics-avg-streak" />
            <StatCard icon={Sparkles} label="Longest Journal Streak" value={growth?.longest_journal_streak ?? 0} color="#2B6B7F" testId="analytics-longest-streak" />
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            {/* Stage distribution pie */}
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <h4 className="text-sm font-semibold text-slate-700 mb-2">Discipleship Stage Distribution</h4>
              <div style={{ width: '100%', height: 260 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={stageData}
                      dataKey="count"
                      nameKey="stage"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      label={(entry) => `${entry.stage}: ${entry.count}`}
                    >
                      {stageData.map((entry) => (
                        <Cell key={entry.stage} fill={STAGE_COLORS[entry.stage] || '#94a3b8'} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend verticalAlign="bottom" height={24} iconSize={10} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Journals per week */}
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <h4 className="text-sm font-semibold text-slate-700 mb-2">Journal Entries — Last 12 Weeks</h4>
              <div style={{ width: '100%', height: 260 }}>
                <ResponsiveContainer>
                  <BarChart data={journalsPerWeek} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} interval={1} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#64748b' }} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#7BA888" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Content Engagement section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl font-heading text-[#2B6B7F] flex items-center gap-2">
            <BookOpen className="w-6 h-6" />
            Content Engagement
          </CardTitle>
          <CardDescription>Most-noted sermons and most-searched Bible topics (last 90 days)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <StatCard icon={BookOpen} label="Total Sermons" value={content?.total_sermons ?? 0} color="#2B6B7F" testId="analytics-total-sermons" />
            <StatCard icon={Sparkles} label="Sermon-note Submissions" value={content?.total_sermon_notes ?? 0} color="#7BA888" testId="analytics-total-sermon-notes" />
            <StatCard icon={Search} label="Bible Searches" value={content?.total_bible_searches ?? 0} color="#5FA8C1" testId="analytics-total-bible-searches" />
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            {/* Top sermons */}
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <SectionHeader icon={BookOpen} color="#2B6B7F" title="Most-noted sermons" />
              {content?.top_sermons?.length ? (
                <ul className="space-y-2" data-testid="top-sermons-list">
                  {content.top_sermons.map((s, i) => (
                    <li key={s.week_date} className="flex items-start gap-3 p-2 rounded-md hover:bg-slate-50">
                      <span className="w-7 h-7 rounded-full bg-[#2B6B7F]/10 text-[#2B6B7F] flex items-center justify-center flex-shrink-0 text-sm font-semibold">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-800 truncate">{s.title}</p>
                        <p className="text-xs text-slate-500">{s.passage} • {fmtShortDate(s.week_date)}</p>
                      </div>
                      <span className="text-sm font-semibold text-[#2B6B7F] flex-shrink-0">{s.note_count}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-slate-500 py-4">No sermon notes submitted yet.</p>
              )}
            </div>

            {/* Top Bible search topics */}
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <SectionHeader icon={Search} color="#5FA8C1" title="Top Bible search topics" />
              {content?.top_search_topics?.length ? (
                <div style={{ width: '100%', height: 260 }} data-testid="top-search-topics-chart">
                  <ResponsiveContainer>
                    <BarChart data={content.top_search_topics} layout="vertical" margin={{ top: 0, right: 16, left: 16, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: '#64748b' }} />
                      <YAxis type="category" dataKey="topic" width={100} tick={{ fontSize: 11, fill: '#475569' }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#5FA8C1" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="text-sm text-slate-500 py-4">No Bible searches recorded yet.</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminAnalytics;
